import { t } from "i18next";
import { useCallback, useMemo } from "react";
import { Control, FieldPath, FieldValues } from "react-hook-form";

import { useCompany } from "#modules/company/view/useCompany";
import { useUpdateCompanySetting } from "#modules/company/view/useUpdateCompanySetting";
import { getUnitFactorsByMaterialType } from "#modules/quoteRequestLines/domain/getUnitFactors";
import { Stack } from "#shared/view/components/Stack";
import { RecommendedPriceInputController } from "#shared/view/designSystem/inputs/RecommendedPriceInputController";
import { isValidUnitFactor, UnitName } from "#shared/view/designSystem/inputs/units";
import { PartTypeType } from "shared";

type Prop<T extends FieldValues> = {
  density: number;
  partType: PartTypeType;
  name: FieldPath<T>;
  control: Control<T>;
  geometry: {
    length: number;
    width: number;
    height: number;
    tubeThickness?: number;
  };
};

export const MaterialRawPrice = <T extends FieldValues>({
  density,
  partType,
  name,
  control,
  geometry,
}: Prop<T>) => {
  const { company } = useCompany();

  const unitFactors = useMemo(
    () =>
      getUnitFactorsByMaterialType({
        partType,
        material: {
          densityKgDm3: density,
          ...geometry,
          tubeThickness: geometry.tubeThickness ?? 0,
        },
      }),
    [geometry, density, partType],
  );

  const getDefaultUnitFactor = useCallback(
    (defaultMaterialPriceUnit: UnitName | undefined) => {
      return unitFactors
        ?.concat({ unit: "€", value: 1 })
        .find((factor) => factor.unit === defaultMaterialPriceUnit);
    },
    [unitFactors],
  );

  const { updateCompanySetting } = useUpdateCompanySetting({
    shouldNotNotify: true,
  });

  const handleUpdateDefaultMaterialPriceUnit = (unit?: UnitName) => {
    updateCompanySetting({
      defaultMaterialPriceUnit: unit,
    });
  };

  const defaultUnitFactor = useMemo(
    () =>
      isValidUnitFactor(company?.companySettings.defaultMaterialPriceUnit, unitFactors || [])
        ? getDefaultUnitFactor(company.companySettings.defaultMaterialPriceUnit)
        : undefined,
    [getDefaultUnitFactor, unitFactors, company?.companySettings.defaultMaterialPriceUnit],
  );

  return (
    <Stack>
      <RecommendedPriceInputController
        name={name}
        label={t("material.price")}
        unit="€"
        control={control}
        onChangeFactor={handleUpdateDefaultMaterialPriceUnit}
        required
        key={defaultUnitFactor?.value.toString()}
        unitFactors={unitFactors}
        defaultUnitFactor={defaultUnitFactor}
      />
    </Stack>
  );
};
